import axios from 'axios';
import { get, post, put, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';
import { ENV } from '../../config/env';

export interface WebsiteServiceItem {
  id: string;
  slug?: string;
  title: string;
  description?: string;
  icon?: string;
  image?: string;
  gallery: string[];
  count: number;
  overview: string[];
  benefits: string[];
  isActive: boolean;
  displayOrder: number;
  categories?: BlogTaxonomyRef[];
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  ogImage?: string;
  canonicalUrl?: string;
  noIndex: boolean;
}

export interface BlogTaxonomyRef {
  id: string;
  name: string;
  slug?: string;
  parentId?: string | null;
}

export interface BlogTaxonomyNode {
  id: string;
  name: string;
  slug?: string;
  parentId?: string | null;
  description?: string;
  isActive: boolean;
  displayOrder: number;
  count: number;
  path: { id: string; name: string; slug: string }[];
  children: BlogTaxonomyNode[];
}

export interface WebsiteBlogPost {
  id: string;
  slug?: string;
  title: string;
  excerpt?: string;
  image?: string;
  publishedAt?: string;
  author: string;
  category?: string;
  categories?: BlogTaxonomyRef[];
  tags: string[];
  tagRefs?: BlogTaxonomyRef[];
  intro?: string;
  content?: string;
  gallery: string[];
  isPublished: boolean;
  displayOrder: number;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  ogImage?: string;
  canonicalUrl?: string;
  noIndex: boolean;
}

/**
 * Website-specific contact settings. Phone, email and address are read from
 * General Settings and returned by the API for display only — they are not
 * editable here.
 */
export interface WebsiteContactSettings {  id?: string;
  phone?: string;
  phoneAlt: string;
  email?: string;
  address?: string;
  workingHours: string;
  mapEmbedUrl: string;
  mapLinkUrl: string;
  recipientEmail: string;
  isActive: boolean;
}

type Paginated<T> = ApiResponse<T[]> & { meta: PaginationMeta };

/** A single public gallery asset managed from the dashboard. */
export type GalleryItemType = 'image' | 'video';

export interface WebsiteGalleryItem {
  id: string;
  type: GalleryItemType;
  title?: string;
  /** Image URL, or the custom thumbnail URL for video items. */
  image?: string;
  /** YouTube URL (video items only). */
  videoUrl?: string;
  isActive: boolean;
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

/** A single patient testimonial managed from the dashboard. */
export interface WebsiteTestimonial {
  id: string;
  name: string;
  role?: string;
  /** Optional heading shown above the quote. */
  title?: string;
  quote: string;
  /** Author avatar image URL. */
  image?: string;
  rating: number;
  isActive: boolean;
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

/** A pricing plan shown in the home page "Plans & Pricing" section. */
export interface WebsitePlan {
  id: string;
  name: string;
  /** Formatted price shown on the card, e.g. "$30". */
  price: string;
  description?: string;
  features: string[];
  /** Highlights the plan with a "Popular" badge. */
  popular: boolean;
  isActive: boolean;
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export const websiteService = {
  // ── Services ──────────────────────────────────────────────
  getServices: (
    page = 1,
    limit = 20,
    search?: string,
    status?: string,
  ): Promise<Paginated<WebsiteServiceItem>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    return get<WebsiteServiceItem[]>(`/website/services?${params.toString()}`) as Promise<
      Paginated<WebsiteServiceItem>
    >;
  },
  getService: (id: string): Promise<ApiResponse<WebsiteServiceItem>> =>
    get<WebsiteServiceItem>(`/website/services/${id}`),
  createService: (data: any): Promise<ApiResponse<WebsiteServiceItem>> =>
    post<WebsiteServiceItem>('/website/services', data),
  updateService: (id: string, data: any): Promise<ApiResponse<WebsiteServiceItem>> =>
    patch<WebsiteServiceItem>(`/website/services/${id}`, data),
  deleteService: (id: string): Promise<ApiResponse<void>> => del<void>(`/website/services/${id}`),
  bulkDeleteServices: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> =>
    post<{ deleted: string[]; notFound: string[] }>('/website/services/bulk-delete', { ids }),

  // ── Blogs ─────────────────────────────────────────────────
  getBlogs: (
    page = 1,
    limit = 20,
    search?: string,
    status?: string,
    categoryId?: string,
  ): Promise<Paginated<WebsiteBlogPost>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (categoryId) params.set('categoryId', categoryId);
    return get<WebsiteBlogPost[]>(`/website/blogs?${params.toString()}`) as Promise<
      Paginated<WebsiteBlogPost>
    >;
  },
  getBlog: (id: string): Promise<ApiResponse<WebsiteBlogPost>> =>
    get<WebsiteBlogPost>(`/website/blogs/${id}`),
  createBlog: (data: any): Promise<ApiResponse<WebsiteBlogPost>> =>
    post<WebsiteBlogPost>('/website/blogs', data),
  updateBlog: (id: string, data: any): Promise<ApiResponse<WebsiteBlogPost>> =>
    patch<WebsiteBlogPost>(`/website/blogs/${id}`, data),
  deleteBlog: (id: string): Promise<ApiResponse<void>> => del<void>(`/website/blogs/${id}`),
  bulkDeleteBlogs: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> =>
    post<{ deleted: string[]; notFound: string[] }>('/website/blogs/bulk-delete', { ids }),

  // ── Blog Categories (hierarchical) ────────────────────────
  getBlogCategories: (includeInactive = true): Promise<ApiResponse<BlogTaxonomyNode[]>> =>
    get<BlogTaxonomyNode[]>(`/website/blog-categories?includeInactive=${includeInactive}`),
  createBlogCategory: (data: any): Promise<ApiResponse<BlogTaxonomyNode>> =>
    post<BlogTaxonomyNode>('/website/blog-categories', data),
  updateBlogCategory: (id: string, data: any): Promise<ApiResponse<BlogTaxonomyNode>> =>
    patch<BlogTaxonomyNode>(`/website/blog-categories/${id}`, data),
  deleteBlogCategory: (id: string): Promise<ApiResponse<void>> =>
    del<void>(`/website/blog-categories/${id}`),

  // ── Blog Tags (hierarchical) ──────────────────────────────
  getBlogTags: (includeInactive = true): Promise<ApiResponse<BlogTaxonomyNode[]>> =>
    get<BlogTaxonomyNode[]>(`/website/blog-tags?includeInactive=${includeInactive}`),
  createBlogTag: (data: any): Promise<ApiResponse<BlogTaxonomyNode>> =>
    post<BlogTaxonomyNode>('/website/blog-tags', data),
  updateBlogTag: (id: string, data: any): Promise<ApiResponse<BlogTaxonomyNode>> =>
    patch<BlogTaxonomyNode>(`/website/blog-tags/${id}`, data),
  deleteBlogTag: (id: string): Promise<ApiResponse<void>> => del<void>(`/website/blog-tags/${id}`),

  // ── Contact ───────────────────────────────────────────────
  getContact: (): Promise<ApiResponse<WebsiteContactSettings>> =>
    get<WebsiteContactSettings>('/website/contact'),
  updateContact: (data: Partial<WebsiteContactSettings>): Promise<ApiResponse<WebsiteContactSettings>> =>
    put<WebsiteContactSettings>('/website/contact', data),

  // ── Gallery ───────────────────────────────────────────────
  getGalleryItems: (
    page = 1,
    limit = 24,
    search?: string,
    type?: string,
    status?: string,
  ): Promise<Paginated<WebsiteGalleryItem>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (type) params.set('type', type);
    if (status) params.set('status', status);
    return get<WebsiteGalleryItem[]>(`/website/gallery?${params.toString()}`) as Promise<
      Paginated<WebsiteGalleryItem>
    >;
  },
  createGalleryItem: (data: any): Promise<ApiResponse<WebsiteGalleryItem>> =>
    post<WebsiteGalleryItem>('/website/gallery', data),
  updateGalleryItem: (id: string, data: any): Promise<ApiResponse<WebsiteGalleryItem>> =>
    patch<WebsiteGalleryItem>(`/website/gallery/${id}`, data),
  deleteGalleryItem: (id: string): Promise<ApiResponse<void>> =>
    del<void>(`/website/gallery/${id}`),
  bulkDeleteGalleryItems: (
    ids: string[],
  ): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> =>
    post<{ deleted: string[]; notFound: string[] }>('/website/gallery/bulk-delete', { ids }),

  // ── Testimonials ──────────────────────────────────────────
  getTestimonials: (
    page = 1,
    limit = 20,
    search?: string,
    status?: string,
  ): Promise<Paginated<WebsiteTestimonial>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    return get<WebsiteTestimonial[]>(`/website/testimonials?${params.toString()}`) as Promise<
      Paginated<WebsiteTestimonial>
    >;
  },
  createTestimonial: (data: any): Promise<ApiResponse<WebsiteTestimonial>> =>
    post<WebsiteTestimonial>('/website/testimonials', data),
  updateTestimonial: (id: string, data: any): Promise<ApiResponse<WebsiteTestimonial>> =>
    patch<WebsiteTestimonial>(`/website/testimonials/${id}`, data),
  deleteTestimonial: (id: string): Promise<ApiResponse<void>> =>
    del<void>(`/website/testimonials/${id}`),
  bulkDeleteTestimonials: (
    ids: string[],
  ): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> =>
    post<{ deleted: string[]; notFound: string[] }>('/website/testimonials/bulk-delete', { ids }),

  // ── Plans ─────────────────────────────────────────────────
  getPlans: (
    page = 1,
    limit = 20,
    search?: string,
    status?: string,
  ): Promise<Paginated<WebsitePlan>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    return get<WebsitePlan[]>(`/website/plans?${params.toString()}`) as Promise<
      Paginated<WebsitePlan>
    >;
  },
  createPlan: (data: any): Promise<ApiResponse<WebsitePlan>> =>
    post<WebsitePlan>('/website/plans', data),
  updatePlan: (id: string, data: any): Promise<ApiResponse<WebsitePlan>> =>
    patch<WebsitePlan>(`/website/plans/${id}`, data),
  deletePlan: (id: string): Promise<ApiResponse<void>> => del<void>(`/website/plans/${id}`),
  bulkDeletePlans: (
    ids: string[],
  ): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> =>
    post<{ deleted: string[]; notFound: string[] }>('/website/plans/bulk-delete', { ids }),

  // ── Media ─────────────────────────────────────────────────
  uploadImage: async (file: File): Promise<ApiResponse<{ url: string }>> => {
    const formData = new FormData();
    formData.append('file', file);
    const API_BASE_URL = `${ENV.VITE_API_URL}${ENV.VITE_API_BASE_PATH || '/api/v1'}`;
    const response = await axios.post<ApiResponse<{ url: string }>>(
      `${API_BASE_URL}/website/media/upload`,
      formData,
      { withCredentials: true, timeout: 60000 },
    );
    return response.data;
  },
};
